import { useEffect, useRef, useCallback } from "react";

/**
 * Global inactivity timer. Tracks the last user-interaction timestamp in
 * localStorage so every tab shares one source of truth — activity in any
 * tab keeps every tab alive.
 *
 * Decisions:
 * - Single setTimeout re-armed on activity (cheap; scales).
 * - Activity writes throttled to 1Hz (avoid hammering localStorage on mousemove).
 * - On fire we re-check `Date.now() - lastActivityAt` because background tabs /
 *   sleeping devices throttle timers; the wall-clock check is authoritative.
 * - Visibility/pageshow handlers force an immediate check on resume so iOS PWA /
 *   WebView returning from background can't bypass the rule.
 */

export const ACTIVITY_KEY = "acex:lastActivityAt";
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "touchmove",
  "scroll",
  "wheel",
  "click",
] as const;

export function bumpActivity() {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export function clearActivity() {
  try {
    localStorage.removeItem(ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
}

export function getLastActivity(): number {
  try {
    const v = localStorage.getItem(ACTIVITY_KEY);
    const n = v ? Number(v) : NaN;
    return Number.isFinite(n) ? n : Date.now();
  } catch {
    return Date.now();
  }
}

interface Options {
  enabled: boolean;
  timeoutMs?: number;
  warningMs?: number; // how long BEFORE timeout to fire warning
  onTimeout: () => void;
  onWarn?: () => void;
}

export function useInactivityTimeout({
  enabled,
  timeoutMs = 24 * 60 * 60 * 1000,
  warningMs = 5 * 60 * 1000,
  onTimeout,
  onWarn,
}: Options) {
  const onTimeoutRef = useRef(onTimeout);
  const onWarnRef = useRef(onWarn);
  const warnedRef = useRef(false);
  const lastWriteRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const warnTimerRef = useRef<number | null>(null);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);
  useEffect(() => {
    onWarnRef.current = onWarn;
  }, [onWarn]);

  const clearTimers = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (warnTimerRef.current !== null) {
      window.clearTimeout(warnTimerRef.current);
      warnTimerRef.current = null;
    }
  }, []);

  const arm = useCallback(() => {
    clearTimers();
    const last = getLastActivity();
    const elapsed = Date.now() - last;
    const remaining = timeoutMs - elapsed;
    if (remaining <= 0) {
      onTimeoutRef.current?.();
      return;
    }
    const warnIn = remaining - warningMs;
    if (warnIn > 0 && !warnedRef.current) {
      warnTimerRef.current = window.setTimeout(() => {
        warnedRef.current = true;
        onWarnRef.current?.();
      }, warnIn);
    } else if (warnIn <= 0 && !warnedRef.current) {
      warnedRef.current = true;
      onWarnRef.current?.();
    }
    timerRef.current = window.setTimeout(() => {
      // Re-verify against wall clock — covers throttled timers / sleep.
      if (Date.now() - getLastActivity() >= timeoutMs) {
        onTimeoutRef.current?.();
      } else {
        arm();
      }
    }, remaining);
  }, [clearTimers, timeoutMs, warningMs]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Seed activity if missing so we don't immediately log out a fresh login.
    if (!localStorage.getItem(ACTIVITY_KEY)) bumpActivity();
    warnedRef.current = false;
    arm();

    const onActivity = () => {
      const now = Date.now();
      // Throttle writes to 1Hz
      if (now - lastWriteRef.current >= 1000) {
        lastWriteRef.current = now;
        bumpActivity();
      }
      if (warnedRef.current) warnedRef.current = false;
      arm();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Mobile/PWA resume — enforce immediately.
        if (Date.now() - getLastActivity() >= timeoutMs) {
          onTimeoutRef.current?.();
          return;
        }
        arm();
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === ACTIVITY_KEY) arm(); // another tab bumped activity
    };

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onVisibility);
    window.addEventListener("storage", onStorage);

    return () => {
      clearTimers();
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onVisibility);
      window.removeEventListener("storage", onStorage);
    };
  }, [enabled, arm, clearTimers, timeoutMs]);
}
